/**
 * SecurityDashboard Component
 *
 * Main security overview with score, vulnerabilities, and scan actions
 */

import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Shield,
  AlertTriangle,
  Download,
  Play,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { SecurityScoreCard } from './SecurityScoreCard';
import {
  useSecurityScore,
  useSecurityScans,
  useSBOM,
  useTriggerScan,
} from '@/hooks/useSecurity';
import type { SecurityScan } from '@forgenetwork/types';

interface SecurityDashboardProps {
  repositoryId: string;
}

/**
 * Scan status configurations
 */
const scanStatusConfig = {
  pending: {
    icon: Clock,
    label: 'Pending',
    color: 'text-yellow-600',
    bg: 'bg-yellow-100',
  },
  running: {
    icon: Loader2,
    label: 'Running',
    color: 'text-blue-600',
    bg: 'bg-blue-100',
  },
  completed: {
    icon: CheckCircle2,
    label: 'Completed',
    color: 'text-green-600',
    bg: 'bg-green-100',
  },
  failed: {
    icon: XCircle,
    label: 'Failed',
    color: 'text-red-600',
    bg: 'bg-red-100',
  },
};

/**
 * Format date for display
 */
function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format duration
 */
function formatDuration(start: Date | string, end?: Date | string): string {
  const startTime = typeof start === 'string' ? new Date(start) : start;
  const endTime = end
    ? typeof end === 'string'
      ? new Date(end)
      : end
    : new Date();

  const diff = endTime.getTime() - startTime.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);

  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

/**
 * Recent scans list
 */
function RecentScans({ scans }: { scans: SecurityScan[] }) {
  if (scans.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No scans have been run yet
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {scans.slice(0, 5).map((scan) => {
        const config = scanStatusConfig[scan.status];
        const Icon = config.icon;

        return (
          <div
            key={scan.id}
            className="flex items-center justify-between p-3 border rounded-lg"
          >
            <div className="flex items-center gap-3">
              <div className={cn('p-2 rounded-lg', config.bg)}>
                <Icon
                  className={cn('h-4 w-4', config.color, {
                    'animate-spin': scan.status === 'running',
                  })}
                />
              </div>
              <div>
                <div className="font-medium text-sm">
                  {config.label}
                  {scan.status === 'completed' &&
                    scan.score !== undefined && (
                      <span className="ml-2 text-muted-foreground">
                        Score: {scan.score}
                      </span>
                    )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatDate(scan.createdAt)}
                  {scan.startedAt && scan.completedAt && (
                    <span className="ml-2">
                      ({formatDuration(scan.startedAt, scan.completedAt)})
                    </span>
                  )}
                </div>
              </div>
            </div>
            {scan.status === 'completed' && scan.grade && (
              <Badge
                variant={
                  scan.grade === 'A' || scan.grade === 'B'
                    ? 'default'
                    : 'destructive'
                }
              >
                {scan.grade}
              </Badge>
            )}
            {scan.status === 'completed' && scan.vulnerabilitiesFound !== undefined && (
              <Badge
                variant={scan.vulnerabilitiesFound > 0 ? 'destructive' : 'secondary'}
              >
                {scan.vulnerabilitiesFound} vulnerabilities
              </Badge>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * SecurityDashboard - Main security overview
 */
export function SecurityDashboard({
  repositoryId,
}: SecurityDashboardProps): React.JSX.Element {
  const { data: scoreData, isLoading: scoreLoading, error: scoreError } = useSecurityScore(repositoryId);
  const { data: scansData, isLoading: scansLoading } = useSecurityScans(repositoryId);
  const { data: sbomData } = useSBOM(repositoryId);
  const triggerScan = useTriggerScan();

  // Check if there's an active scan
  const hasActiveScan = React.useMemo(() => {
    return scansData?.scans.some(
      (s) => s.status === 'pending' || s.status === 'running'
    );
  }, [scansData]);

  // Handle scan trigger
  const handleTriggerScan = () => {
    triggerScan.mutate(repositoryId, {
      onSuccess: () => {
        // Success handled by query invalidation
      },
      onError: (error) => {
        console.error('Failed to trigger scan:', error);
      },
    });
  };

  // Download SBOM
  const handleDownloadSBOM = () => {
    if (sbomData?.sbom) {
      const blob = new Blob([JSON.stringify(sbomData.sbom, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sbom-${repositoryId}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  if (scoreLoading) {
    return (
      <div className="space-y-6">
        <div className="h-96 bg-muted animate-pulse rounded-xl" />
        <div className="h-64 bg-muted animate-pulse rounded-xl" />
      </div>
    );
  }

  if (scoreError) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          {scoreError instanceof Error
            ? scoreError.message
            : 'Failed to load security data'}
        </AlertDescription>
      </Alert>
    );
  }

  if (!scoreData) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Shield className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Security Data</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Run a security scan to analyze your repository
            </p>
            <Button
              onClick={handleTriggerScan}
              disabled={triggerScan.isPending || hasActiveScan}
              className="gap-2"
            >
              {triggerScan.isPending || hasActiveScan ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Run Security Scan
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Actions Bar */}
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-2xl font-bold">Security Overview</h2>
        <div className="flex items-center gap-2">
          {sbomData?.sbom && (
            <Button
              variant="outline"
              onClick={handleDownloadSBOM}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Download SBOM
            </Button>
          )}
          <Button
            onClick={handleTriggerScan}
            disabled={triggerScan.isPending || hasActiveScan}
            className="gap-2"
          >
            {triggerScan.isPending || hasActiveScan ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                Run Scan
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Security Score Card */}
      <SecurityScoreCard
        score={scoreData.score}
        grade={scoreData.grade}
        breakdown={scoreData.breakdown}
        lastScan={scoreData.lastScan}
      />

      {/* Vulnerability Summary */}
      {scoreData.breakdown.vulnerabilities > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Vulnerability Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Breakdown by severity (view Vulnerabilities tab for details)
            </p>
          </CardContent>
        </Card>
      )}

      {/* Recent Scans */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Scans</CardTitle>
        </CardHeader>
        <CardContent>
          {scansLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <RecentScans scans={scansData?.scans || []} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
